/// <reference path="../index.d.ts" />
// @ts-check

const C = {
  attributes: {
    animate: 'data-animate',
    delay: 'data-animate-delay',
  },
  prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)')
    .matches,
  /**
   * The default animations.
   * @type {Record<string, AnimationConfig>}
   */
  animations: {
    'fade-in-up': {
      name: 'fade-in-up',
      reset: false,
      keyframes: {
        standard: [
          {
            transform: 'translateY(20px)',
            opacity: 0,
          },
          {
            transform: 'translateY(0)',
            opacity: 1,
          },
        ],
        reduced: [
          {
            opacity: 0,
          },
          {
            opacity: 1,
          },
        ],
      },
      initialState: {
        transform: 'translateY(20px)',
        opacity: 0,
      },
    },
    'block-in': {
      name: 'block-in',
      reset: false,
      keyframes: {
        standard: [
          {
            transform: 'translateY(40px)',
          },
          {
            transform: 'translateY(0)',
          },
        ],
        reduced: [],
      },
      initialState: {
        transform: 'translateY(40px)',
      },
    },
  },
  /**
   * The default timings.
   * @type {Object}
   */
  timing: {
    duration: 400,
    easing: 'ease-in-out',
    fill: 'both',
  },
  intersectionThreshold: 0.2,
  rootMargin: '0px 0px -10% 0px',
};

const eleConfigMap = new WeakMap();

const observer = new IntersectionObserver((entries) => {
  for (let i = 0; i < entries.length; i++) {
    const config = eleConfigMap.get(entries[i].target);
    if (config) {
      animationHandler({
        target: entries[i].target,
        intersecting: entries[i].isIntersecting,
        config: config,
      });
      continue;
    }
  }
});

/**
 * Register the observer for data-animate elements.
 * @param {Partial<InitConifg> | undefined} [options]
 */
const initAnimate = (options = {}) => {
  if (options.timing) {
    C.timing = { ...C.timing, ...options.timing };
  }

  const animationElements = document.querySelectorAll(
    `[${C.attributes.animate}]`
  );
  if (animationElements.length === 0) return;

  for (let i = 0; i < animationElements.length; i++) {
    const ele = animationElements[i];
    const targetAnimation = ele.getAttribute(C.attributes.animate);
    if (!targetAnimation) return;
    if (!C.animations[targetAnimation]) return;

    eleConfigMap.set(ele, C.animations[targetAnimation]);

    if (C.animations[targetAnimation]?.initialState) {
      // @ts-expect-error
      Object.assign(ele.style, C.animations[targetAnimation]?.initialState);
    }

    observer.observe(ele);
  }
};

/**
 * Handles the animation for a given element.
 * @param {Object} props
 * @param {Element} props.target
 * @param {boolean} props.intersecting
 * @param {AnimationConfig} props.config
 */
const animationHandler = (props) => {
  const delayAttrVal =
    props.target.getAttribute(C.attributes.delay) || undefined;

  if (props.intersecting) {
    props.target.animate(
      C.prefersReducedMotion
        ? props.config.keyframes.reduced
          ? props.config.keyframes.reduced
          : props.config.keyframes.standard
        : props.config.keyframes.standard,
      {
        duration: props.config.timing?.duration || C.timing.duration,
        easing: props.config.timing?.easing || C.timing.easing,
        fill: props.config.timing?.fill || C.timing.fill,
        delay: delayAttrVal ? parseFloat(delayAttrVal) : undefined, // delay doesnt seem to work?
      }
    );

    if (props.config.reset === false) {
      observer.unobserve(props.target);
    }
  } else {
    if (props.config.reset === true) {
      props.target.getAnimations().forEach((animation) => {
        animation.cancel();
      });
    }
  }
};

/**
 * Register a new animation.
 * @param {AnimationConfig | Partial<AnimationConfig> & { name: string }} animation
 */
const register = (animation) => {
  C.animations[animation.name] = {
    ...C.animations[animation.name],
    ...animation,
  };
};

export default {
  init: initAnimate,
  register: register,
};
